import { useState } from 'react'
import { useQuery } from 'react-query'
import { Modal } from 'antd'
import { PlusIcon } from '@heroicons/react/outline'

import { Loader } from '../../../../components'
import { fetchLastmileXdocks } from '../../../../services'
import { XdockForm } from './XdockForm'

const T_PREFIX = 'lastmile_management.shipping_costs.xdock_tab'
const FORM_PREFIX = 'lastmile_management.shipping_costs.xdock_form'

/**
 * Xdock tab for Cubbo methods: a single "Cross-docks" table listing the physical
 * facilities (name, country, CP, phone) with create / edit via
 * the POST/PATCH /lastmile/xdocks endpoints. Zone → cross-dock assignment lives in
 * the Zonas tab.
 */
export const XdockTab = ({ country, canEdit, t }) => {
  const [xdockForm, setXdockForm] = useState(null) // null=closed; {} = create; {xdock} = edit

  const { data: xdocks = [], isLoading } = useQuery(['lastmile-xdocks', country], () => fetchLastmileXdocks({ country, includeZones: true }), {
    enabled: Boolean(country),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t(`${T_PREFIX}.crossdocks_title`)}</h3>
            <p className="text-sm text-gray-500">{t(`${T_PREFIX}.crossdocks_help`)}</p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setXdockForm({})}
              className="ml-auto inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PlusIcon className="h-4 w-4" />
              {t(`${T_PREFIX}.create_crossdock`)}
            </button>
          )}
        </div>
        {isLoading ? (
          <Loader show={true} />
        ) : xdocks.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">{t(`${T_PREFIX}.crossdocks_empty`)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_xdock`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_country`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_zipcode`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_phone`)}</th>
                  {canEdit && <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_actions`)}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {xdocks.map(x => (
                  <tr key={x.id}>
                    <td className="px-4 py-2 font-medium text-gray-900">{x.name}</td>
                    <td className="px-4 py-2">{x.country || '—'}</td>
                    <td className="px-4 py-2">{x.shipping_zipcode || '—'}</td>
                    <td className="px-4 py-2">{x.phone || '—'}</td>
                    {canEdit && (
                      <td className="px-4 py-2 text-right">
                        <button type="button" onClick={() => setXdockForm({ xdock: x })} className="text-sm font-medium text-indigo-600 hover:text-indigo-900">
                          {t(`${T_PREFIX}.edit`)}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cross-dock create / edit modal */}
      <Modal
        open={!!xdockForm}
        onCancel={() => setXdockForm(null)}
        className="cost-modal"
        title={xdockForm?.xdock ? t(`${FORM_PREFIX}.edit_title`) : t(`${FORM_PREFIX}.create_title`)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <XdockForm xdock={xdockForm?.xdock} defaultCountry={country} onDone={() => setXdockForm(null)} t={t} />
      </Modal>
    </div>
  )
}
